import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    if (this.dataset.scMergeBothIntents === 'true') {
      this.#loadMergedBothIntents(productId, sectionId, id).catch((e) => {
        this.#handleError(e);
      });
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Complementary first, then related — same slider, deduped within URL limit.
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string} componentId
   */
  async #loadMergedBothIntents(productId, sectionId, componentId) {
    const primaryIntent = 'complementary';
    const secondaryIntent = 'related';

    const limitMatch = this.dataset.url?.match(/[?&]limit=(\d+)/);
    const limit =
      limitMatch != null && limitMatch[1] != null
        ? Number.parseInt(limitMatch[1], 10)
        : 10;

    const [primaryResult, secondaryResult] = await Promise.all([
      this.#fetchCachedRecommendations(productId, sectionId, primaryIntent, { parallel: true }),
      this.#fetchCachedRecommendations(productId, sectionId, secondaryIntent, { parallel: true }),
    ]);

    const parser = new DOMParser();
    /**
     * @param {string} primaryHtml
     * @param {string} secondaryHtml
     */
    const mergeIntoInnerHtml = (primaryHtml, secondaryHtml) => {
      const primaryDoc = parser.parseFromString(primaryHtml || '', 'text/html');
      const secondaryDoc = parser.parseFromString(secondaryHtml || '', 'text/html');
      const primaryRoot = primaryDoc.querySelector(`product-recommendations[id="${componentId}"]`);
      const secondaryRoot = secondaryDoc.querySelector(`product-recommendations[id="${componentId}"]`);

      const shellRoot = primaryRoot?.querySelector('slideshow-slide')
        ? primaryRoot
        : secondaryRoot?.querySelector('slideshow-slide')
          ? secondaryRoot
          : primaryRoot?.innerHTML.trim()
            ? primaryRoot
            : secondaryRoot;

      if (!shellRoot?.innerHTML.trim()) return null;

      const shellDoc = shellRoot.ownerDocument;
      const slidesParent = shellRoot.querySelector('slideshow-slides');
      if (!slidesParent) return shellRoot.innerHTML;

      const slidesPrimary = primaryRoot ? [...primaryRoot.querySelectorAll('slideshow-slide')] : [];
      const slidesSecondary = secondaryRoot ? [...secondaryRoot.querySelectorAll('slideshow-slide')] : [];

      const seenPaths = new Set();
      /** @type {Element[]} */
      const merged = [];

      /**
       * @param {Element} slide
       * @param {Document} sourceDoc
       */
      const pushDeduped = (slide, sourceDoc) => {
        const link = slide.querySelector('a[href*="/products/"]');
        const href = link?.getAttribute('href');
        if (!href) return;
        let pathname;
        try {
          pathname = new URL(href, window.location.origin).pathname;
        } catch {
          return;
        }
        if (seenPaths.has(pathname)) return;
        seenPaths.add(pathname);
        const node =
          sourceDoc === shellDoc
            ? /** @type {Element} */ (slide.cloneNode(true))
            : /** @type {Element} */ (shellDoc.importNode(slide, true));
        merged.push(node);
      };

      for (const slide of slidesPrimary) pushDeduped(slide, primaryDoc);
      for (const slide of slidesSecondary) pushDeduped(slide, secondaryDoc);

      const capped = merged.slice(0, limit);
      slidesParent.replaceChildren(...capped);
      capped.forEach((slide, index) => slide.setAttribute('slide-id', String(index + 1)));

      return shellRoot.innerHTML;
    };

    let inner = null;
    if (primaryResult.success && primaryResult.data) {
      inner = mergeIntoInnerHtml(
        primaryResult.data,
        secondaryResult.success && secondaryResult.data ? secondaryResult.data : ''
      );
    }
    if (!inner?.trim() && secondaryResult.success && secondaryResult.data) {
      inner = mergeIntoInnerHtml(
        secondaryResult.data,
        primaryResult.success && primaryResult.data ? primaryResult.data : ''
      );
    }

    if (inner?.trim()) {
      this.dataset.recommendationsPerformed = 'true';
      this.innerHTML = inner;
      return;
    }

    if (!Shopify.designMode) {
      this.#handleError(new Error('No recommendations available'));
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @param {{ parallel?: boolean }} [options]
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent, options = {}) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    /** @type {AbortController} */
    let controller;
    if (options.parallel) {
      controller = new AbortController();
    } else {
      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();
      controller = this.#activeFetch;
    }

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      if (!options.parallel) {
        this.#activeFetch = null;
      }
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
